import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { trackRegistryCardClick } from "../analytics";
import { Search, Star, Zap, ArrowRight, LayoutGrid, Layers, KeyRound, Globe, ShieldCheck, Activity, Link2 } from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import type { RegistryItem } from "../lib/registryApi";
import { useRegistryList, useToggleRegistryStar } from "../lib/registryQueries";
import RegistryConnectModal from "../components/RegistryConnectModal";
import { fetchSessionUser } from "../lib/sessionUser";
import type { SessionUser } from "../lib/sessionUser";
import { readCachedSessionUser } from "../lib/sessionCache";

const CATEGORY_COLORS: Record<string, string> = {
  productivity:  "bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-900",
  finance:       "bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-900",
  development:   "bg-violet-50 text-violet-600 border-violet-100 dark:bg-violet-950/50 dark:text-violet-400 dark:border-violet-900",
  entertainment: "bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-950/50 dark:text-rose-400 dark:border-rose-900",
  science:       "bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-900",
  games:         "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100 dark:bg-fuchsia-950/50 dark:text-fuchsia-400 dark:border-fuchsia-900",
};

const categoryColor = (cat: string) =>
  CATEGORY_COLORS[cat.toLowerCase()] ??
  "bg-muted text-muted-foreground border-border";

type RegistryCardProps = {
  mcp: RegistryItem;
  index: number;
  sessionUser: SessionUser | null;
  onConnect: (mcp: RegistryItem) => void;
};

const RegistryCard = ({ mcp, index, sessionUser, onConnect }: RegistryCardProps) => {
  const navigate = useNavigate();
  const starMutation = useToggleRegistryStar(mcp.slug);

  const openDetails = () => {
    trackRegistryCardClick(mcp.slug, mcp.name);
    navigate(`/registry/${mcp.slug}`);
  };

  const handleStarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    starMutation.mutate();
  };

  const handleConnect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onConnect(mcp);
  };

  const needsAuth = mcp.authType && mcp.authType !== "none";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.3) }}
    >
      <div
        role="link"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDetails();
          }
        }}
        className="group flex flex-col h-full glass-strong rounded-2xl p-7 card-lift shadow-premium border border-transparent hover:border-primary/15 transition-colors cursor-pointer"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            {mcp.icon ? (
              <div className="w-11 h-11 rounded-xl bg-muted/60 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-blue-50 dark:group-hover:bg-blue-950/40 transition-colors">
                {mcp.icon}
              </div>
            ) : null}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <h2 className="text-base font-bold text-foreground group-hover:text-primary transition-colors leading-snug">
                  {mcp.name}
                </h2>
                {mcp.verified && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 shrink-0" title="Verified by API to MCP">
                    <ShieldCheck className="w-2.5 h-2.5" /> Verified
                  </span>
                )}
              </div>
            </div>
          </div>
          <span
            className={`flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border capitalize ${categoryColor(mcp.category)}`}
          >
            {mcp.category}
          </span>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1 line-clamp-2">
          {mcp.description}
        </p>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground font-medium flex-wrap">
            <button
              type="button"
              onClick={handleStarClick}
              disabled={starMutation.isPending}
              aria-pressed={mcp.isStarred}
              aria-label={mcp.isStarred ? `Unstar ${mcp.name}` : `Star ${mcp.name}`}
              className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-amber-600 transition hover:bg-amber-50 disabled:opacity-60 dark:hover:bg-amber-950/30"
            >
              <Star
                className="w-3.5 h-3.5"
                fill={mcp.isStarred ? "currentColor" : "none"}
                strokeWidth={mcp.isStarred ? 0 : 2}
              />
              {(mcp.starCount ?? 0).toLocaleString()}
            </button>
            {(mcp.endpointCount ?? 0) > 0 && (
              <span className="flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-primary/60" />
                {mcp.endpointCount} endpoints
              </span>
            )}
            {(mcp.weeklyCallCount ?? 0) > 0 && (
              <span className="flex items-center gap-1 text-violet-600 dark:text-violet-400" title="Tool calls this week">
                <Activity className="w-3.5 h-3.5" />
                {(mcp.weeklyCallCount ?? 0).toLocaleString()} calls/wk
              </span>
            )}
            {needsAuth ? (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" title={`Needs your ${mcp.authLabel ?? "API key"}`}>
                <KeyRound className="w-3.5 h-3.5" />
                API key needed
              </span>
            ) : (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400" title="No authentication needed">
                <Globe className="w-3.5 h-3.5" />
                Public
              </span>
            )}
          </div>
          {needsAuth && sessionUser ? (
            <button
              type="button"
              onClick={handleConnect}
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              <Link2 className="w-3 h-3" /> Connect
            </button>
          ) : (
            <span className="flex items-center gap-1 text-xs font-semibold text-primary/70 opacity-0 group-hover:opacity-100 transition-opacity">
              View details <ArrowRight className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const RegistryPage = () => {
  const { data: registry = [], isLoading, error } = useRegistryList();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [connectTarget, setConnectTarget] = useState<RegistryItem | null>(null);

  useEffect(() => {
    void fetchSessionUser().then(setSessionUser);
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(registry.map((m) => m.category.toLowerCase())));
    return ["all", ...cats.sort()];
  }, [registry]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return registry.filter((m) => {
      const matchesQuery =
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.description.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q);
      const matchesCat =
        activeCategory === "all" || m.category.toLowerCase() === activeCategory;
      return matchesQuery && matchesCat;
    });
  }, [registry, query, activeCategory]);

  return (
    <>
      <TopHeader />
      <main className="min-h-screen">
        {/* ── Hero header ── */}
        <div className="relative overflow-hidden bg-background border-b border-border/50">
          <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
          <motion.div
            animate={{ x: [0, 20, 0], y: [0, -12, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-0 right-0 w-[400px] h-[300px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, hsl(214 100% 93% / 0.6) 0%, transparent 70%)" }}
          />
          <div className="max-w-5xl mx-auto px-6 pt-14 pb-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 mb-4">
                <Layers className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-widest">Registry</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-1">
                MCP Registry
              </h1>
              <p className="text-muted-foreground text-base max-w-xl">
              Browse MCP servers and explore stats, features, and comments.
              </p>
            </motion.div>

            {/* ── Search ── */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative mt-8 max-w-md"
            >
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search MCPs…"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/30 transition shadow-sm"
              />
            </motion.div>

            {/* ── Category chips ── */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="flex flex-wrap gap-2 mt-4"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all capitalize ${
                    activeCategory === cat
                      ? "bg-primary text-white border-primary shadow-sm"
                      : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="max-w-5xl mx-auto px-6 py-10">
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="glass-strong rounded-2xl p-7 animate-pulse h-44" />
              ))}
            </div>
          )}
          {error && (
            <p className="text-red-500 text-sm">
              {error instanceof Error ? error.message : "Failed to load registry"}
            </p>
          )}

          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              <LayoutGrid className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No MCPs match your search.</p>
              <button
                type="button"
                className="mt-3 text-sm text-primary hover:underline"
                onClick={() => { setQuery(""); setActiveCategory("all"); }}
              >
                Clear filters
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((mcp, i) => (
                <RegistryCard key={mcp.slug} mcp={mcp} index={i} sessionUser={sessionUser} onConnect={setConnectTarget} />
              ))}
            </AnimatePresence>
          </div>

          {!isLoading && filtered.length > 0 && (
            <p className="text-center text-xs text-muted-foreground mt-10">
              Showing {filtered.length} of {registry.length} MCP servers
            </p>
          )}
        </div>
      </main>
      <Footer />
      {connectTarget && sessionUser && (
        <RegistryConnectModal
          mcp={connectTarget}
          user={sessionUser}
          onClose={() => setConnectTarget(null)}
        />
      )}
    </>
  );
};

export default RegistryPage;
